import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { configuration } from '../configration';
import { Observable, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthenticationService } from './authentication.service';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { SKIP_SPINNER } from './spinner.service';

const AUTH = configuration.AUTH_KEY;

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  url: string;
  constructor(
    public http: HttpClient,
    public router: Router,
    private authService: AuthenticationService,
  ) {}

  get<T>(request: Request): Observable<T | any> {
    // need to remove any type once everywhere define type
    return this.http
      .get<T>(configuration.BASE_URL + request.path, {
        headers: this.getHeader(),
      })
      .pipe(
        tap((result: any) => {
          if (result['token']) {
            let data = this.authService.getAuthDetail();
            data = {
              ...data,
              token: result['token'],
            };
            this.authService.setAuth(data);
          }
        }),
      );
  }

  delete<T>(request: Request): Observable<T | any> {
    // need to remove any type once everywhere define type
    return this.http
      .delete<T>(configuration.BASE_URL + request.path, {
        headers: this.getHeader(),
      })
      .pipe(
        tap((result: any) => {
          if (result['token']) {
            let data = this.authService.getAuthDetail();
            data = {
              ...data,
              token: result['token'],
            };
            this.authService.setAuth(data);
          }
        }),
      );
  }

  downloadFile(data: any, download_fileName?: string) {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    let a = document.createElement('a');

    a.href = url;

    a.target = '_blank';

    a.download = download_fileName || 'Exported_.xlsx';

    document.body.appendChild(a);

    a.click();
  }
  getPDF(request: Request): Observable<Blob> {
    this.url = configuration.BASE_URL + request.path;
    let authorization = this.authService.getToken();
    let reqHeader: HttpHeaders = new HttpHeaders({});
    reqHeader = reqHeader.append('TOKEN', [authorization]);

    return this.http.get<Blob>(this.url, {
      headers: reqHeader,
      responseType: 'blob' as 'json',
    });
  }
  viewPDF(request: Request): Observable<Blob> {
    this.url = configuration.BASE_URL + request.path;
    let authorization = this.authService.getToken();
    let reqHeader: HttpHeaders = new HttpHeaders({});
    reqHeader = reqHeader.append('TOKEN', [authorization]);

    return this.http.post<Blob>(this.url, request['data'], {
      headers: reqHeader,
      responseType: 'blob' as 'json',
    });
  }

  getExcel(request: Request): Observable<Blob> {
    this.url = configuration.BASE_URL + request['path'];

    const token = this.authService.getToken();
    let reqHeader: HttpHeaders = new HttpHeaders({});

    reqHeader = reqHeader.append('TOKEN', [token]);
    return this.http.post<Blob>(this.url, request['data'], {
      headers: reqHeader,
      responseType: 'blob' as 'json',
    });
  }
  downloadPdf(request: Request): Observable<Blob> {
    this.url = configuration.BASE_URL + request['path'];

    const token = this.authService.getToken();
    let reqHeader: HttpHeaders = new HttpHeaders({});

    reqHeader = reqHeader.append('TOKEN', [token]);

    return this.http.post<Blob>(this.url, request['data'], {
      headers: reqHeader,
      responseType: 'blob' as 'json',
    });
  }
  downloadUpdatedFile(request: Request): Observable<Blob> {
    this.url = configuration.BASE_URL + request['path'];
    let authorization = this.authService.getToken();
    let reqHeader: HttpHeaders = new HttpHeaders({});
    reqHeader = reqHeader.append('TOKEN', [authorization]);

    return this.http.get<Blob>(this.url, {
      headers: reqHeader,
      responseType: 'blob' as 'json',
    });
  }

  post<T>(request: Request): Observable<T | any> {
    let headers = this.getHeader();
    if (typeof request['data'] === 'string') {
      headers = headers.set('Content-Type', 'text/plain');
    }
    // need to remove any type once everywhere define type
    return this.http
      .post<T | any[]>(configuration.BASE_URL + request['path'], request['data'], {
        headers: headers,
        context: new HttpContext().set(SKIP_SPINNER, !!request.silent),
      })
      .pipe(
        map((result: any = []) => {
          if (result['token']) {
            let data = this.authService.getAuthDetail();
            data = {
              ...data,
              token: result['token'],
            };
            this.authService.setAuth(data);
          }
          return result;
        }),
        retry(1),
        catchError(this.handleError.bind(this)),
      );
  }

  put<T>(request: Request): Observable<T | any> {
    // need to remove any type once everywhere define type
    return this.http
      .put<T | any[]>(configuration.BASE_URL + request['path'], request['data'], {
        headers: this.getHeader(),
      })
      .pipe(
        map((result: any = []) => {
          if (result['token']) {
            let data = this.authService.getAuthDetail();
            data = {
              ...data,
              token: result['token'],
            };
            this.authService.setAuth(data);
          }
          return result;
        }),
        retry(1),
        catchError(this.handleError.bind(this)),
      );
  }

  handleError(error: any) {
    if (error['error'] != undefined) {
      if (
        error['error']['status']['code'] &&
        error['error']['status']['code'] == 'SESSION_EXPIRED'
      ) {
        localStorage.removeItem(AUTH);
        window.location.href = '/';
      }
    }
    return throwError(error);
  }

  private getHeader() {
    const token = this.authService.getToken();
    let header: HttpHeaders = new HttpHeaders({});
    header = header.append(
      'Access-Control-Allow-Methods',
      'GET, POST, OPTIONS, PUT, PATCH, DELETE',
    );

    if (!!this.authService.getToken()) {
      header = header.append('Access-Control-Allow-Origin', '*');
      header = header.append('TOKEN', [token]);
    }
    return header;
  }

  postImage(request: Request) {
    let headers = new HttpHeaders();
    const token = this.authService.getToken();
    if (request.isAuth) {
      headers = new HttpHeaders({ TOKEN: token });
    }
    headers.set('Content-Type', 'multipart/form-data');
    return this.http
      .post(configuration.BASE_URL + request.path, request.data, {
        headers: headers,
      })
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }
}

export interface Request {
  path: string;
  data?: any;
  isAuth?: boolean;
  /** When true, this call does not trigger the global spinner. */
  silent?: boolean;
}
